import PhoneInput from "./PhoneInput";
import { formatPhone } from "../../utils/phoneFormat";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    createFamily,
    deleteFamily,
    getFamilies,
    importFamilies,
    updateFamily,
} from "../../services/hrOpsApi";
import { useAlertContext } from "../../context/AlertContext";
import { formatDateRu } from "../../utils/dateFormat";

const EMPTY_FORM = { employee_id: "", full_name: "", relationship: "", gender: "male", birth_date: "", phone_number: "", create_birthday_events: true };
const ALL_RELATIONSHIPS = "__all__";
const MISSING_RELATIONSHIP = "__missing__";

const normalizeFilterValue = (value) => String(value || "")
    .toLocaleLowerCase("ru-RU")
    .replace(/ё/g, "е")
    .replace(/\s+/g, " ")
    .trim();

export default function FamilySection({ employees = [], employeeId = null }) {
    const { showAlert } = useAlertContext();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ ...EMPTY_FORM, employee_id: employeeId ? String(employeeId) : "" });
    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState("");
    const [relationshipFilter, setRelationshipFilter] = useState(ALL_RELATIONSHIPS);
    const importRef = useRef(null);

    const normalizedEmployees = useMemo(() => {
        const map = new Map();
        employees.forEach((employee) => {
            const id = employee.employee_id || employee.id;
            if (id) map.set(String(id), { id, full_name: employee.full_name });
        });
        return [...map.values()].sort((a, b) => String(a.full_name).localeCompare(String(b.full_name), "ru"));
    }, [employees]);
    const employeeById = useMemo(
        () => new Map(normalizedEmployees.map((employee) => [String(employee.id), employee])),
        [normalizedEmployees]
    );
    const relationshipOptions = useMemo(() => {
        const groups = new Map();
        items.forEach((item) => {
            const label = String(item.relationship || "").trim();
            const value = normalizeFilterValue(label) || MISSING_RELATIONSHIP;
            const current = groups.get(value);
            groups.set(value, {
                value,
                label: current?.label || label || "Не указано",
                count: (current?.count || 0) + 1,
            });
        });
        return [...groups.values()].sort((a, b) => {
            if (a.value === MISSING_RELATIONSHIP) return 1;
            if (b.value === MISSING_RELATIONSHIP) return -1;
            return a.label.localeCompare(b.label, "ru");
        });
    }, [items]);
    const filteredItems = useMemo(() => {
        const query = normalizeFilterValue(search);
        return items.filter((item) => {
            const relationship = normalizeFilterValue(item.relationship) || MISSING_RELATIONSHIP;
            if (relationshipFilter !== ALL_RELATIONSHIPS && relationship !== relationshipFilter) return false;
            if (!query) return true;
            const employeeName = employeeById.get(String(item.employee_id))?.full_name || "";
            return normalizeFilterValue([
                item.full_name,
                employeeName,
                item.relationship,
                item.phone_number,
            ].join(" ")).includes(query);
        });
    }, [employeeById, items, relationshipFilter, search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setItems(await getFamilies(employeeId) || []);
        } catch (error) {
            showAlert(error.message || String(error), "error");
        } finally {
            setLoading(false);
        }
    }, [employeeId, showAlert]);

    useEffect(() => { void load(); }, [load]);

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM, employee_id: employeeId ? String(employeeId) : "" });
        setShowForm(true);
    };

    const openEdit = (item) => {
        setEditingId(item.id);
        setForm({
            employee_id: String(item.employee_id),
            full_name: item.full_name || "",
            relationship: item.relationship || "",
            gender: item.gender || "male",
            birth_date: item.birth_date || "",
            phone_number: item.phone_number || "",
            create_birthday_events: Boolean(item.birthday_calendar_enabled),
        });
        setShowForm(true);
    };

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        const payload = { ...form, employee_id: Number(form.employee_id), relationship: form.relationship.trim() || null, phone_number: form.phone_number.trim() || null };
        try {
            if (editingId) await updateFamily(editingId, payload);
            else await createFamily(payload);
            await load();
            setShowForm(false);
            showAlert(editingId ? "Данные родственника обновлены" : "Родственник добавлен", "success");
        } catch (error) {
            showAlert(error.message || String(error), "error");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Удалить родственника «${item.full_name}»?`)) return;
        try {
            await deleteFamily(item.id);
            setItems((current) => current.filter((row) => row.id !== item.id));
            showAlert("Родственник удалён", "success");
        } catch (error) {
            showAlert(error.message || String(error), "error");
        }
    };

    const upload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setImporting(true);
        try {
            const result = await importFamilies(file);
            await load();
            const skipped = result?.skipped || 0;
            const updated = result?.updated || 0;
            showAlert(
                `Добавлено родственников: ${result?.created || 0}. Обновлено: ${updated}${skipped ? `. Без изменений: ${skipped}` : ""}`,
                "success"
            );
        } catch (error) {
            showAlert(error.message || String(error), "error");
        } finally {
            setImporting(false);
        }
    };

    return (
        <section className="space-y-4" data-testid="family-section">
            <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div>
                    <h2 className="text-lg font-semibold">Родственники сотрудников</h2>
                    <p className="mt-1 text-xs text-slate-500">
                        Для XLSX обязательны колонки «ID сотрудника», «ФИО», «Пол», «Дата рождения»; «Родство» и «Телефон» необязательны.
                    </p>
                </div>
                <div className="flex gap-2">
                    {!employeeId ? <>
                        <input ref={importRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={upload} data-testid="family-import-input" />
                        <button type="button" disabled={importing} onClick={() => importRef.current?.click()} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium disabled:opacity-50" data-testid="import-families">
                            {importing ? "Загрузка…" : "Загрузить из Excel"}
                        </button>
                    </> : null}
                    <button type="button" onClick={openCreate} className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white" data-testid="add-family">Добавить родственника</button>
                </div>
            </div>

            <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[minmax(240px,1fr)_minmax(220px,320px)_auto] md:items-end" data-testid="family-filters">
                <label className="text-sm">
                    <span className="mb-1 block font-medium text-slate-700">Поиск</span>
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="ФИО, сотрудник, родство или телефон"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20"
                        data-testid="family-search"
                    />
                </label>
                <label className="text-sm">
                    <span className="mb-1 block font-medium text-slate-700">Кем является</span>
                    <select
                        value={relationshipFilter}
                        onChange={(event) => setRelationshipFilter(event.target.value)}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-[#cda834] focus:ring-2 focus:ring-[#cda834]/20"
                        data-testid="family-relationship-filter"
                    >
                        <option value={ALL_RELATIONSHIPS}>Все категории ({items.length})</option>
                        {relationshipOptions.map((option) => <option key={option.value} value={option.value}>{option.label} ({option.count})</option>)}
                    </select>
                </label>
                <div className="flex items-center justify-between gap-3 md:justify-end">
                    <span className="whitespace-nowrap text-xs text-slate-500" data-testid="family-filter-count">Показано: {filteredItems.length} из {items.length}</span>
                    {(search || relationshipFilter !== ALL_RELATIONSHIPS) ? <button type="button" onClick={() => { setSearch(""); setRelationshipFilter(ALL_RELATIONSHIPS); }} className="text-xs font-medium text-slate-600 hover:underline" data-testid="family-reset-filters">Сбросить</button> : null}
                </div>
            </div>

            {showForm ? (
                <form onSubmit={submit} className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2" data-testid="family-form">
                    {!employeeId ? <label className="text-sm"><span className="mb-1 block font-medium">Сотрудник</span><select required value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="w-full rounded-xl border p-2.5" data-testid="family-employee"><option value="">Выберите сотрудника</option>{normalizedEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.full_name} (ID {employee.id})</option>)}</select></label> : null}
                    <label className="text-sm"><span className="mb-1 block font-medium">ФИО</span><input required maxLength={300} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full rounded-xl border p-2.5" data-testid="family-full-name" /></label>
                    <label className="text-sm"><span className="mb-1 block font-medium">Кем является</span><input maxLength={100} placeholder="Например: дочь, супруг" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className="w-full rounded-xl border p-2.5" data-testid="family-relationship" /></label>
                    <label className="text-sm"><span className="mb-1 block font-medium">Пол</span><select required value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full rounded-xl border p-2.5" data-testid="family-gender"><option value="male">Мужской</option><option value="female">Женский</option></select></label>
                    <label className="text-sm"><span className="mb-1 block font-medium">Дата рождения</span><input required type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="w-full rounded-xl border p-2.5" data-testid="family-birth-date" /></label>
                    <label className="text-sm"><span className="mb-1 block font-medium">Телефон</span><PhoneInput maxLength={20} value={form.phone_number} onChange={(value) => setForm({ ...form, phone_number: value })} className="w-full rounded-xl border p-2.5" data-testid="family-phone" /></label>
                    <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm"><input type="checkbox" checked={form.create_birthday_events} onChange={(e) => setForm({ ...form, create_birthday_events: e.target.checked })} data-testid="family-create-birthday-event" />Создать ежегодное событие в календаре</label>
                    <div className="flex items-end gap-2"><button type="submit" disabled={saving} className="rounded-xl bg-[#cda834] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50" data-testid="save-family">{saving ? "Сохранение…" : "Сохранить"}</button><button type="button" onClick={() => setShowForm(false)} className="rounded-xl border px-4 py-2.5 text-sm">Отмена</button></div>
                </form>
            ) : null}

            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600"><tr>{!employeeId ? <th className="p-3 text-left">Сотрудник</th> : null}<th className="p-3 text-left">Родственник</th><th className="p-3 text-left">Кем является</th><th className="p-3 text-left">Пол</th><th className="p-3 text-left">Дата рождения</th><th className="p-3 text-left">Телефон</th><th className="p-3" /></tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan={employeeId ? 6 : 7} className="p-6 text-center text-slate-400">Загрузка…</td></tr> : null}
                        {!loading && !items.length ? <tr><td colSpan={employeeId ? 6 : 7} className="p-6 text-center text-slate-400">Родственники пока не добавлены</td></tr> : null}
                        {!loading && items.length > 0 && !filteredItems.length ? <tr><td colSpan={employeeId ? 6 : 7} className="p-6 text-center text-slate-400">По заданным условиям ничего не найдено</td></tr> : null}
                        {!loading && filteredItems.map((item) => <tr key={item.id} className="border-t border-slate-100">
                            {!employeeId ? <td className="p-3"><Link className="font-medium hover:underline" to={`/employees/${item.employee_id}`}>{employeeById.get(String(item.employee_id))?.full_name || `Сотрудник #${item.employee_id}`}</Link></td> : null}
                            <td className="p-3 font-medium">{item.full_name}</td><td className="p-3">{item.relationship || "—"}</td><td className="p-3">{item.gender === "female" ? "Женский" : "Мужской"}</td><td className="p-3">{formatDateRu(item.birth_date)}</td><td className="p-3">{formatPhone(item.phone_number) || "—"}</td>
                            <td className="p-3 text-right"><button type="button" className="mr-3 text-xs text-slate-600 hover:underline" onClick={() => openEdit(item)}>Редактировать</button><button type="button" className="text-xs text-red-600 hover:underline" onClick={() => remove(item)}>Удалить</button></td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
